import { BUILDING_LABELS, RESOURCES, UNIT_LABELS, type UnitType } from '@bienenkampf/shared';
import { Button, Input } from '@bienenkampf/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { formatDuration, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import { liveResources } from './ResourceBar.js';
import type { HiveView } from './types.js';

const ICON: Record<UnitType, string> = {
  FORAGER: '🐝',
  SCOUT: '🔭',
  GUARD: '🛡',
  WARRIOR: '⚔',
  HEATER: '🔥',
  NURSE: '🍼',
};
const ROLE: Record<UnitType, string> = {
  FORAGER: 'Sammelt auf Blütenfeldern (Phase 3).',
  SCOUT: 'Späht fremde Stöcke aus, sehr schnell.',
  GUARD: 'Verteidigt den Stock.',
  WARRIOR: 'Greift an, trägt wenig.',
  HEATER: 'Verteidigt, dreifach stark gegen Kriegerinnen.',
  NURSE: 'Beschleunigt die Ausbildung.',
};

export function UnitList({ view, now }: { view: HiveView; now: Date }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const live = liveResources(view, now);
  const [counts, setCounts] = useState<Partial<Record<UnitType, string>>>({});
  const enqueue = useMutation(
    trpc.hive.train.enqueue.mutationOptions({
      onSuccess: (_d, vars) => setCounts((c) => ({ ...c, [vars.type]: '' })),
      onSettled: () =>
        qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId: view.id }) }),
    }),
  );
  const free = view.population.cap - view.population.current;
  const queueFull = view.trainQueue.length >= view.trainQueueSlots;
  const winter = !view.season.broodEnabled;

  return (
    <div className="flex flex-col gap-3">
      {winter && (
        <p className="rounded-lg bg-sky-500/10 px-3 py-2 text-sm text-sky-300">
          Winter: Die Brut ruht, keine Ausbildung möglich.
        </p>
      )}
      <ul className="flex flex-col divide-y divide-stone-800">
        {view.units.map((u) => {
          const maxByRes = Math.min(
            ...RESOURCES.map((r) => (u.cost[r] > 0 ? Math.floor(live[r] / u.cost[r]) : Infinity)),
          );
          const max = Math.max(0, Math.min(maxByRes, free));
          const locked = u.missing.length > 0;
          const raw = counts[u.type] ?? '';
          const n = Math.max(0, Math.floor(Number(raw) || 0));
          const affordable = n > 0 && n <= max;
          return (
            <li key={u.type} className="flex flex-col gap-2 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium">
                      {ICON[u.type]} {UNIT_LABELS[u.type]}
                    </span>
                    <span className="text-sm tabular-nums text-stone-300">
                      {formatNumber(u.count)}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500">{ROLE[u.type]}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs tabular-nums text-stone-400">
                    <span>⚔ {u.attack}</span>
                    <span>🛡 {u.defense}</span>
                    {u.gatherPower > 0 && <span>🌼 {u.gatherPower}</span>}
                    {u.carry > 0 && <span>🎒 {u.carry}</span>}
                    <span>🕐 {u.minPerHex} min/Hex</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs tabular-nums">
                    {RESOURCES.filter((r) => u.cost[r] > 0).map((r) => (
                      <span
                        key={r}
                        className={live[r] >= u.cost[r] ? 'text-stone-300' : 'text-red-400'}
                      >
                        {r === 'nectar' ? '🍯' : r === 'pollen' ? '🌼' : '🕯'}{' '}
                        {formatNumber(u.cost[r])}
                      </span>
                    ))}
                    <span className="text-stone-400">
                      ⏱ {formatDuration(u.trainSeconds)} / Biene
                    </span>
                  </div>
                  {locked && (
                    <div className="mt-1 text-xs text-amber-400">
                      Braucht{' '}
                      {u.missing.map((m) => `${BUILDING_LABELS[m.building]} ${m.level}`).join(', ')}
                    </div>
                  )}
                </div>
                {!locked && (
                  <form
                    className="flex items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (affordable) enqueue.mutate({ hiveId: view.id, type: u.type, count: n });
                    }}
                  >
                    <div className="w-24">
                      <Input
                        name={`train-${u.type}`}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={Math.max(1, max)}
                        placeholder={`max ${max}`}
                        value={raw}
                        onChange={(e) => setCounts((c) => ({ ...c, [u.type]: e.target.value }))}
                        className="text-right"
                        aria-label={`Anzahl ${UNIT_LABELS[u.type]}`}
                      />
                    </div>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!affordable || queueFull || winter || enqueue.isPending}
                      title={
                        queueFull
                          ? 'Ausbildungs-Queue voll'
                          : max === 0
                            ? 'Keine Ressourcen oder kein Platz'
                            : undefined
                      }
                    >
                      Ausbilden
                    </Button>
                  </form>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {enqueue.error && <p className="text-sm text-red-400">{enqueue.error.message}</p>}
    </div>
  );
}
